import type { Review } from '@/types/review';
import { mockReviews } from '@/constants/mock-data';

declare global {
  interface Window {
    ictportal_user_reviews?: {
      locale?: string;
      logged_in?: boolean;
    };
    _IP21_USER_REVIEWS_CONFIG?: {
      key: string;
      product: string;
      title: string;
    };
  }
}

export interface WordPressApiResponse {
  success: boolean;
  data?: {
    reviews: Review[];
    total: number;
    message?: string;
  };
  error?: string;
  message?: string;
}

export interface SubmitReviewResponse {
  success: boolean;
  data?: {
    review: Review;
    message?: string;
  };
  error?: string;
  message?: string;
}

class WordPressApiService {
  private baseUrl: string;
  private ajaxUrl: string;
  private isMockMode = false;

  constructor() {
    // WordPress AJAX URL - typically /wp-admin/admin-ajax.php
    this.baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    this.ajaxUrl = `${this.baseUrl}/wp-admin/admin-ajax.php`;
  }

  private getIdentifier(): string {
    if (typeof window === 'undefined') return 'mock';

    const identifier = window._IP21_USER_REVIEWS_CONFIG?.key;

    if (!identifier) {
      console.warn(
        'No identifier found in window._IP21_USER_REVIEWS_CONFIG.key, using mock mode'
      );
      return 'mock';
    }

    return identifier;
  }

  private async simulateApiDelay(): Promise<void> {
    // Simulate network delay for mock mode
    await new Promise((resolve) =>
      setTimeout(resolve, 800 + Math.random() * 400)
    );
  }

  private async makeRequest<T>(
    action: string,
    data: Record<string, any> = {}
  ): Promise<T> {
    const identifier = this.getIdentifier();
    this.isMockMode = identifier === 'mock';

    // Handle mock mode
    if (this.isMockMode) {
      return this.handleMockRequest<T>(action, data);
    }

    const formData = new FormData();
    formData.append('action', action);
    formData.append('identifier', identifier);

    // Add additional data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(
          key,
          typeof value === 'object' ? JSON.stringify(value) : String(value)
        );
      }
    });

    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin', // Include cookies for WordPress authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success && result.error) {
        throw new Error(result.error);
      }

      return result as T;
    } catch (error) {
      console.error(`WordPress API Error (${action}):`, error);
      throw error;
    }
  }

  private async handleMockRequest<T>(
    action: string,
    data: Record<string, any> = {}
  ): Promise<T> {
    await this.simulateApiDelay();

    switch (action) {
      case 'get_user_reviews_by_product':
        return this.getMockReviews(data.product_key) as T;

      case 'submit_user_review':
        return this.submitMockReview(data.review_data, data.product_key) as T;

      case 'mark_review_helpful':
        return this.markMockHelpful(data.review_id) as T;

      default:
        throw new Error(`Unknown mock action: ${action}`);
    }
  }

  private getMockReviews(productKey?: string): WordPressApiResponse {
    console.log(
      '🎭 Mock Mode: Fetching reviews for',
      productKey || 'all products'
    );

    let filteredReviews = mockReviews.filter(
      (review) => review.status === 'approved'
    );

    if (productKey) {
      filteredReviews = filteredReviews.filter(
        (review) => review.productKey === productKey
      );
    }

    return {
      success: true,
      data: {
        reviews: filteredReviews,
        total: filteredReviews.length,
        message: 'Mock data loaded successfully',
      },
    };
  }

  private submitMockReview(
    reviewData: any,
    productName: string
  ): SubmitReviewResponse {
    console.log('🎭 Mock Mode: Submitting review for', productName, reviewData);

    // Parse pros and cons (from string to array)
    const pros = reviewData.pros
      ? (reviewData.pros || '').split(/\n+/).filter(Boolean)
      : [];
    const cons = reviewData.cons
      ? (reviewData.cons || '').split(/\n+/).filter(Boolean)
      : [];

    // Create new mock review
    const newReview: Review = {
      id: `mock_${Date.now()}`,
      name: reviewData.name || 'Anonymous',
      role: reviewData.role || 'User',
      company: reviewData.company || 'Company',
      companySize: reviewData.companySize || 'Unknown',
      sector: reviewData.sector || 'Unknown',
      rating: Number(reviewData.rating) || 5,
      title: reviewData.title || 'Review',
      fullReview: reviewData.fullReview || reviewData.full_review || '',
      pros,
      cons,
      helpful: 0,
      createdAt: new Date().toISOString(),
      productKey: productName,
      status: 'approved',
      verified: false,
      userType: reviewData.userType || 'Current user',
    };

    return {
      success: true,
      data: {
        review: newReview,
        message: 'Mock review submitted successfully',
      },
    };
  }

  private markMockHelpful(reviewId: string): {
    success: boolean;
    data: { helpful: number };
  } {
    console.log('🎭 Mock Mode: Marking review as helpful', reviewId);

    // Simulate incrementing helpful count
    const currentHelpful = Math.floor(Math.random() * 20) + 1;

    return {
      success: true,
      data: {
        helpful: currentHelpful + 1,
      },
    };
  }

  async getReviews(productKey?: string): Promise<Review[]> {
    try {
      const response = await this.makeRequest<WordPressApiResponse>(
        'get_user_reviews_by_product',
        {
          product_key: productKey,
        }
      );

      if (response.success && response.data?.reviews) {
        // Validate and transform the data
        return this.validateReviews(response.data.reviews);
      }

      throw new Error(
        response.error || response.message || 'Failed to fetch reviews'
      );
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  }

  async submitReview(reviewData: any, productKey: string): Promise<Review> {
    try {
      const response = await this.makeRequest<SubmitReviewResponse>(
        'submit_user_review',
        {
          review_data: reviewData,
          product_key: productKey,
          source: window.location.href,
        }
      );

      if (response.success && response.data?.review) {
        return this.validateReview(response.data.review);
      }

      throw new Error(
        response.error || response.message || 'Failed to submit review'
      );
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  async markHelpful(reviewId: string): Promise<boolean> {
    try {
      const response = await this.makeRequest<{
        success: boolean;
        data?: { helpful: number };
        error?: string;
      }>('mark_review_helpful', {
        id: reviewId,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to mark review as helpful');
      }

      return true;
    } catch (error) {
      console.error('Error marking review as helpful:', error);
      throw error;
    }
  }

  // Public method to check if in mock mode
  public isMock(): boolean {
    return this.getIdentifier() === 'mock';
  }

  private validateReview(review: any): Review {
    // Ensure the review object has all required fields with proper types
    const pros = Array.isArray(review.pros)
      ? review.pros
      : review.pros
      ? (review.pros || '').split(/\n+/).filter(Boolean)
      : [];
    const cons = Array.isArray(review.cons)
      ? review.cons
      : review.cons
      ? (review.cons || '').split(/\n+/).filter(Boolean)
      : [];
    return {
      id: String(review.id || ''),
      name: String(review.name || ''),
      role: String(review.role || ''),
      company: String(review.company || ''),
      companySize: String(review.companySize || review.company_size || ''),
      sector: String(review.sector || ''),
      rating: Number(review.rating) || 0,
      title: String(review.title || ''),
      fullReview: String(review.fullReview || review.full_review || ''),
      pros: pros,
      cons: cons,
      avatar: review.avatar || undefined,
      helpful: Number(review.helpful) || 0,
      createdAt: String(
        review.createdAt || review.created_at || new Date().toISOString()
      ),
      productKey: String(review.productKey || review.product_key || ''),
      status:
        review.status === 'approved' ||
        review.status === 'pending' ||
        review.status === 'rejected'
          ? review.status
          : 'pending',
      moderatedAt: review.moderatedAt || review.moderated_at || undefined,
      moderatedBy: review.moderatedBy || review.moderated_by || undefined,
      userType: review.userType || review.user_type || undefined,
      verified: Boolean(review.verified) && review.verified !== '0',
    };
  }

  private validateReviews(reviews: any[]): Review[] {
    if (!Array.isArray(reviews)) {
      console.warn('Reviews data is not an array:', reviews);
      return [];
    }

    return reviews
      .map((review) => {
        try {
          return this.validateReview(review);
        } catch (error) {
          console.warn('Invalid review data:', review, error);
          return null;
        }
      })
      .filter((review): review is Review => review !== null);
  }
}

// Export singleton instance
export const wordpressApi = new WordPressApiService();
